import type { Operation } from '@apollo/client';
import { onError } from '@apollo/client/link/error';
import { tokenUtility } from '@fluentsms/auth';
import type { GraphQLFormattedError, OperationDefinitionNode } from 'graphql';

// Whitelist of safe ID fields to include in error logging
const SAFE_ID_FIELDS = [
  // Core entity IDs
  'id',
  'orderId',
  'userId',
  'tenantId',

  // Business entity IDs
  'titleId',
  'partyId',
  'propertyId',
  'documentId',
  'settlementId',
  'underwriterId',
  'policyId',

  // Related entity IDs
  'buyerId',
  'sellerId',
  'lenderId',
  'agentId',
  'borrowerId',
  'contactId',
  'addressId',
  'noteId',
  'taskId',
  'workflowId',

  // Transaction IDs
  'transactionId',
  'paymentId',
  'disbursementId',
  'depositId',
  'refundId',

  // Template and configuration IDs
  'templateId',
  'formId',
  'reportId',
  'configId',
  'ruleId',

  // Administrative IDs
  'roleId',
  'groupId',
  'permissionId',
  'rumSessionId',
  'pageId',
  'traceparent',
  'tracestate',
  'correlationId',
];

// Extract only whitelisted ID fields from variables
const extractSafeIds = (variables: Record<string, unknown> | unknown[]): Record<string, unknown> => {
  if (!variables || typeof variables !== 'object') {
    return {};
  }

  if (Array.isArray(variables)) {
    // For arrays, extract IDs from each object
    return {
      arrayLength: variables.length,
      items: variables.map((item) => extractSafeIds(item as Record<string, unknown>)),
    };
  }

  const safeIds: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(variables)) {
    // Check if this field is a whitelisted ID field
    const isIdField = SAFE_ID_FIELDS.some(
      (safeField) =>
        key.toLowerCase() === safeField.toLowerCase() || key.toLowerCase().endsWith(safeField.toLowerCase()),
    );

    if (isIdField) {
      if (value && typeof value === 'object' && !Array.isArray(value)) {
        // Recursively extract IDs from nested objects
        safeIds[key] = extractSafeIds(value as Record<string, unknown>);
      } else {
        // Include primitive ID values
        safeIds[key] = value;
      }
    } else if (value && typeof value === 'object') {
      // For non-ID fields, still recursively check nested objects for IDs
      const nestedIds = extractSafeIds(value as Record<string, unknown>);
      if (Object.keys(nestedIds).length > 0) {
        safeIds[key] = nestedIds;
      }
    }
  }

  return safeIds;
};

// Extract safe user context from token without logging the token itself
const extractSafeUserContext = () => {
  try {
    const accessToken = tokenUtility.getAccessToken();
    if (!accessToken || !tokenUtility.validateToken(accessToken)) {
      return { status: 'invalid_token' };
    }

    // Get decoded token payload safely using existing tokenUtility methods
    const decodedToken = tokenUtility.getDecodedDetailToken();
    const completeToken = tokenUtility.getCompleteDecodedToken();

    if (!decodedToken || !completeToken) {
      return { status: 'token_decode_failed' };
    }

    // Extract only safe, non-sensitive fields
    return {
      userId: decodedToken.userId || completeToken.sub || 'unknown',
      tenantId: decodedToken.tenantId || 'unknown',
      displayName: decodedToken.displayName || 'unknown',
      securityVersion: decodedToken.securityVersion || 'unknown',
    };
  } catch (error) {
    return {
      status: 'extraction_error',
      error: error instanceof Error ? error.message : 'unknown_error',
    };
  }
};

// Extract correlation ID from operation context
const extractCorrelationId = (operation: Operation) => {
  try {
    const context = operation.getContext();
    const correlationObject = context?.headers?.['correlation-object'];
    if (typeof correlationObject === 'string') {
      const parsed = JSON.parse(correlationObject);
      return parsed.correlationId || null;
    }
    return null;
  } catch {
    return null;
  }
};

// Determine operation type (query/mutation/subscription) from the AST
const getOperationType = (op: Operation): 'query' | 'mutation' | 'subscription' | 'unknown' => {
  try {
    const def = op.query.definitions.find((d) => d.kind === 'OperationDefinition') as
      | OperationDefinitionNode
      | undefined;
    return def?.operation ?? 'unknown';
  } catch {
    return 'unknown';
  }
};

// Create enhanced context for error logging
const createEnhancedContext = (operation: Operation) => ({
  timestamp: new Date().toISOString(),
  url: typeof window !== 'undefined' ? window.location.pathname + window.location.search : 'unknown',
  correlationId: extractCorrelationId(operation),
  userContext: extractSafeUserContext(),
  operationName: operation.operationName,
  operationType: getOperationType(operation),
  operationKind: operation.query?.definitions?.[0]?.kind,
  safeIdsFromVariables: extractSafeIds(operation.variables || {}),
});

// Build a safe subset of GraphQL extensions to avoid leaking server internals
const buildSafeGraphQLExtensions = (
  extensions: Record<string, unknown> | undefined,
): Record<string, unknown> | undefined => {
  if (!extensions || typeof extensions !== 'object') return undefined;
  const allowedKeys = ['code', 'correlationId', 'requestId'];
  const safe: Record<string, unknown> = {};
  allowedKeys.forEach((k) => {
    if (k in extensions) safe[k] = (extensions as Record<string, unknown>)[k];
  });
  return Object.keys(safe).length ? safe : undefined;
};

// Build a safe network error payload (no bodies/responses)
const buildSafeNetworkError = (networkError: Error & { statusCode?: number; response?: unknown }) => {
  const safe: { message: string; statusCode?: number; name?: string; stack?: string } = {
    message: networkError.message,
    statusCode: networkError.statusCode,
    name: networkError.name,
  };

  return safe;
};

const isClientError = (code: unknown): boolean => typeof code === 'number' && code >= 400 && code < 500;

// Logging functions for GraphQL errors
const logGraphQLErrors = (graphQLErrors: readonly GraphQLFormattedError[], operation: Operation) => {
  const enhancedContext = createEnhancedContext(operation);

  graphQLErrors.forEach((error) => {
    const clientError = isClientError(error.extensions?.code);
    const logFn = clientError ? console.warn : console.error;
    const message = clientError ? 'GraphQL Warning:' : 'GraphQL Error:';
    logFn(message, {
      ...enhancedContext,
      error: {
        message: error.message,
        locations: error.locations,
        path: error.path,
        extensions: buildSafeGraphQLExtensions(error.extensions as unknown as Record<string, unknown>),
      },
    });
  });
};

const logNetworkError = (networkError: Error & { statusCode?: number; response?: unknown }, operation: Operation) => {
  const enhancedContext = createEnhancedContext(operation);
  const { statusCode } = networkError;
  const logFn = isClientError(statusCode) ? console.warn : console.error;
  logFn('Network Error:', {
    ...enhancedContext,
    error: buildSafeNetworkError(networkError),
  });
};

export const errorLoggingLink = onError(({ graphQLErrors, networkError, operation }) => {
  if (graphQLErrors) {
    // Backend GraphQL errors
    logGraphQLErrors(graphQLErrors, operation);
  }

  if (networkError) {
    // Network errors (400, 500, etc.) - captures requests that fail before hitting GraphQL handler
    logNetworkError(networkError, operation);
  }
});
